import { Component, EventEmitter, Input, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { firstValueFrom } from 'rxjs';
import { setLoading } from 'src/app/redux/actions/user.actions';
import { AppState } from 'src/app/redux/app.state';
import { NotificationService } from 'src/app/services/notification.service';
import { extractApiErrorMessage } from 'src/app/shared/utils/extract-api-error-message.util';
import { Product, Property } from 'src/typings';
import { PropertyService } from '../property.service';

@Component({
  selector: 'app-create',
  templateUrl: './create.component.html',
  styleUrls: ['./create.component.scss']
})
export class CreateComponent {
  @Input() product: Product | undefined;

  @Output() changeEvent: EventEmitter<
    {
      id: number;
      value: string;
      property: Property;
    }[]
  > = new EventEmitter();

  @Input() set prop_row(value: { id: number; value: string; property: Property } | undefined) {
    if (!value) return;
    this._prop_row = value;
    this.createForm.controls.property.setValue(value.property);
    this.createForm.controls.value.setValue(value.value);
  }

  get prop_row() {
    return this._prop_row;
  }

  _prop_row: { id: number; value: string; property: Property } | undefined;

  set loading(value: boolean) {
    this.store.dispatch(setLoading({ payload: value }));
  }

  constructor(
    private fb: UntypedFormBuilder,
    private propertyService: PropertyService,
    private store: Store<AppState>,
    private notificationService: NotificationService
  ) {}

  createForm: UntypedFormGroup = this.fb.group({
    id: new UntypedFormControl(),
    property: new UntypedFormControl(),
    value: new UntypedFormControl()
  });

  get propertyControl() {
    return this.createForm.controls.property as UntypedFormControl;
  }

  get valueControl() {
    return this.createForm.controls.value as UntypedFormControl;
  }

  get property() {
    return this.createForm.value.property;
  }

  private getPreparedPayload(): {
    propertyId: number;
    value: string;
  } | null {
    const property = this.propertyControl.value as Property | null;
    const value = String(this.valueControl.value || '')
      .replace(/\s+/g, ' ')
      .trim();

    if (!property?.id) {
      this.notificationService.warning('Выберите характеристику');
      return null;
    }

    if (!value) {
      this.notificationService.warning('Значение характеристики не может быть пустым');
      return null;
    }

    const duplicateExists = (this.product?.properties || []).some((row) => {
      if (row.property.id !== property.id) {
        return false;
      }

      if (this.prop_row && row.id === this.prop_row.id) {
        return false;
      }

      return row.value.replace(/\s+/g, ' ').trim().toUpperCase() === value.toUpperCase();
    });

    if (duplicateExists) {
      this.notificationService.warning('Такое значение характеристики уже есть у товара');
      return null;
    }

    this.valueControl.setValue(value);
    return {
      propertyId: property.id,
      value
    };
  }

  async create() {
    if (!this.product) return console.error('Не задан товар!');
    const preparedPayload = this.getPreparedPayload();
    if (!preparedPayload) return;
    this.loading = true;
    try {
      const res = await firstValueFrom(
        this.propertyService.addNewPropertyToProduct(
          this.product.id,
          preparedPayload.propertyId,
          preparedPayload.value
        )
      );
      this.changeEvent.emit(res);
    } catch (err) {
      console.error(err);
      this.notificationService.warning(
        extractApiErrorMessage(err, 'Не удалось сохранить характеристику')
      );
    }
    this.loading = false;
  }

  async update() {
    if (!this.prop_row) return;
    const preparedPayload = this.getPreparedPayload();
    if (!preparedPayload) return;
    this.loading = true;
    try {
      const res = await firstValueFrom(
        this.propertyService.updatePropertyOfProduct(this.prop_row.id, preparedPayload.value)
      );
      this.changeEvent.emit(res);
    } catch (err) {
      console.error(err);
      this.notificationService.warning(
        extractApiErrorMessage(err, 'Не удалось обновить характеристику')
      );
    }
    this.loading = false;
  }

  save() {
    if (this.prop_row) return this.update();
    return this.create();
  }
}
